import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { useEffect, useRef, useState } from "react";
import {
  appendMessage,
  createThread,
  deleteThread,
  getThread,
  listThreads,
  mockAssistantReply,
  type CoachThread,
} from "@/lib/coach-store";
import { getCoachUsage, fetchCoachUsage, incrementCoachUsage } from "@/lib/coach-quota";
import { useSubscription } from "@/hooks/useSubscription";
import { ArrowLeft, Plus, Send, Sparkles, Trash2, Crown } from "lucide-react";

export const Route = createFileRoute("/_authenticated/coach/$threadId")({
  head: () => ({ meta: [{ title: "AI Coach — LendyTrack" }] }),
  component: CoachChat,
});

function CoachChat() {
  const { threadId } = Route.useParams();
  const navigate = useNavigate();
  const { isPremium } = useSubscription();
  const [thread, setThread] = useState<CoachThread | undefined>(undefined);
  const [threads, setThreads] = useState<CoachThread[]>([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [remaining, setRemaining] = useState<number>(() => (typeof window !== "undefined" ? getCoachUsage().remaining : 3));
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = getThread(threadId);
    if (!t) {
      const nt = createThread();
      navigate({ to: "/coach/$threadId", params: { threadId: nt.id }, replace: true });
      return;
    }
    setThread(t);
    setThreads(listThreads());
    fetchCoachUsage().then((u) => setRemaining(u.remaining));
    inputRef.current?.focus();
  }, [threadId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [thread?.messages.length, typing]);

  if (!thread) return <AppShell><div className="p-8 text-muted-foreground">Loading...</div></AppShell>;

  const overLimit = !isPremium && remaining <= 0;

  const send = async () => {
    const text = input.trim();
    if (!text || typing) return;
    if (!isPremium) {
      const r = await incrementCoachUsage();
      if (!r.allowed) {
        setRemaining(0);
        return;
      }
      setRemaining(r.remaining);
    }
    setInput("");
    const updated = appendMessage(threadId, { role: "user", content: text });
    if (updated) setThread({ ...updated });
    setTyping(true);
    setTimeout(() => {
      const reply = mockAssistantReply(text);
      const t2 = appendMessage(threadId, { role: "assistant", content: reply });
      if (t2) setThread({ ...t2 });
      setThreads(listThreads());
      setTyping(false);
      inputRef.current?.focus();
    }, 700);
  };

  const startNew = () => {
    const t = createThread();
    navigate({ to: "/coach/$threadId", params: { threadId: t.id } });
  };


  return (
    <AppShell>
      <div className="grid h-[calc(100vh-9rem)] gap-4 lg:grid-cols-[280px_1fr]">
        {/* Thread list */}
        <aside className="hidden flex-col rounded-2xl border border-border bg-card p-3 shadow-soft lg:flex">
          <button onClick={startNew} className="mb-3 inline-flex items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90">
            <Plus className="h-4 w-4" /> New chat
          </button>
          <div className="flex-1 space-y-1 overflow-y-auto">
            {threads.map((t) => (
              <div key={t.id} className={`group flex items-center gap-1 rounded-md px-1 ${t.id === threadId ? "bg-secondary" : "hover:bg-muted"}`}>
                <Link
                  to="/coach/$threadId"
                  params={{ threadId: t.id }}
                  className="flex-1 truncate px-2 py-2 text-sm"
                >
                  {t.title}
                </Link>
                <button
                  onClick={() => {
                    deleteThread(t.id);
                    const remaining = listThreads();
                    setThreads(remaining);
                    if (t.id === threadId) {
                      if (remaining[0]) navigate({ to: "/coach/$threadId", params: { threadId: remaining[0].id }, replace: true });
                      else navigate({ to: "/coach" });
                    }
                  }}
                  className="hidden rounded p-1.5 text-muted-foreground hover:bg-danger/10 hover:text-danger group-hover:block"
                  aria-label="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </aside>

        {/* Chat */}
        <div className="flex min-h-0 flex-col rounded-2xl border border-border bg-card shadow-soft">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div className="flex items-center gap-3">
              <Link to="/coach" className="lg:hidden"><ArrowLeft className="h-4 w-4" /></Link>
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-accent text-accent-foreground">
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <div className="font-display text-base font-semibold">{thread.title}</div>
                <div className="text-[11px] text-muted-foreground">Educational only · Not financial or legal advice</div>
              </div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-5 overflow-y-auto px-5 py-6">
            {thread.messages.map((m) => (
              <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-gradient-accent text-accent-foreground">
                    <Sparkles className="h-4 w-4" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex gap-3">
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-gradient-accent text-accent-foreground">
                  <Sparkles className="h-4 w-4 animate-pulse" />
                </div>
                <div className="rounded-2xl bg-muted px-4 py-3 text-sm text-muted-foreground">Thinking…</div>
              </div>
            )}
          </div>

          <div className="border-t border-border p-3">
            {!isPremium && (
              <div className={`mb-2 flex items-center justify-between rounded-md px-3 py-1.5 text-xs ${overLimit ? "bg-danger/10 text-danger" : "bg-muted text-muted-foreground"}`}>
                <span>
                  {overLimit
                    ? "Daily free limit reached. Upgrade for unlimited coaching."
                    : `${remaining} of 3 free messages remaining today`}
                </span>
                <Link to="/pricing" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
                  <Crown className="h-3 w-3" /> Upgrade
                </Link>
              </div>
            )}
            <div className="flex items-end gap-2 rounded-xl border border-input bg-background p-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={overLimit ? "Upgrade to Premium to keep chatting…" : "Ask about APR, fees, a specific lender, or debt strategy…"}
                rows={2}
                disabled={overLimit}
                className="flex-1 resize-none bg-transparent px-2 py-1 text-sm focus:outline-none disabled:opacity-50"
              />
              <button
                onClick={send}
                disabled={!input.trim() || typing || overLimit}
                className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-40"
                aria-label="Send"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {["What is APR?", "Is OppLoans safe?", "Snowball vs avalanche?", "How do I raise my credit score?"].map((s) => (
                <button
                  key={s}
                  onClick={() => setInput(s)}
                  className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground hover:bg-secondary"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

        </div>
      </div>
    </AppShell>
  );
}
